import { Controller } from "@hotwired/stimulus"

const NAV_KEYS = new Set(["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", "Tab", "Enter"])
const NON_EDIT_INPUT_TYPES = new Set([
  "checkbox",
  "hidden",
  "radio",
  "submit",
  "button",
  "reset",
  "file"
])

export default class extends Controller {
  static values = {
    estimateId: Number
  }

  connect() {
    this.lastSelectedInput = null
    this.enterSaveInput = null
    this.boundFocusIn = (event) => this.handleFocusIn(event)
    this.boundKeydown = (event) => this.handleKeydown(event)
    this.boundInput = (event) => this.handlePricingCategoryMutation(event)
    this.boundClick = (event) => this.handleCellClick(event)
    this.boundMouseDown = (event) => this.handleInputMouseDown(event)
    this.boundSaveFocusNavigation = (event) => {
      if (event.target !== this.enterSaveInput) this.enterSaveInput = null
    }
    this.saveFocusObserver = new MutationObserver(() => this.restoreSaveFocus())
    this.saveFocusObserver.observe(this.element, { childList: true, subtree: true })

    this.element.addEventListener("focusin", this.boundFocusIn)
    this.element.addEventListener("keydown", this.boundKeydown)
    this.element.addEventListener("input", this.boundInput)
    this.element.addEventListener("click", this.boundClick)
    this.element.addEventListener("mousedown", this.boundMouseDown)
    document.addEventListener("pointerdown", this.boundSaveFocusNavigation)
    document.addEventListener("focusin", this.boundSaveFocusNavigation)
  }

  disconnect() {
    this.element.removeEventListener("focusin", this.boundFocusIn)
    this.element.removeEventListener("keydown", this.boundKeydown)
    this.element.removeEventListener("input", this.boundInput)
    this.element.removeEventListener("click", this.boundClick)
    this.element.removeEventListener("mousedown", this.boundMouseDown)
    document.removeEventListener("pointerdown", this.boundSaveFocusNavigation)
    document.removeEventListener("focusin", this.boundSaveFocusNavigation)
    this.saveFocusObserver.disconnect()
    this.lastSelectedInput = null
    this.enterSaveInput = null
  }

  handleInputMouseDown(event) {
    if (event.defaultPrevented || event.button !== 0 || !this.isEstimateItemInput(event.target)) return
    if (event.target === this.lastSelectedInput) return

    // Only subsequent clicks within the last selected input use native caret placement.
    event.preventDefault()
    this.focusInput(event.target)
  }

  handleCellClick(event) {
    const target = event.target
    if (event.defaultPrevented || event.button !== 0 || !(target instanceof Element)) return
    if (this.isEstimateItemInput(target)) {
      if (target !== this.lastSelectedInput) this.focusInput(target)
      return
    }
    if (target.closest("input, select, textarea, button, a, label, [contenteditable], .drag_handle, .drag-handle")) return

    const cell = target.closest(".navigable-cell")
    if (!cell || !this.element.contains(cell)) return
    const input = Array.from(cell.querySelectorAll("input")).find((candidate) => (
      this.isEstimateItemInput(candidate) && candidate.offsetParent !== null
    ))
    if (!input) return

    this.focusInput(input)
  }

  handlePricingCategoryMutation(event) {
    const target = event.target
    if (!(target instanceof HTMLInputElement)) return
    if (!target.matches('form.edit_estimate_item input[name="estimate_item[pricing_category]"]')) return

    const checker = target.closest("form")?.querySelector('input[name="estimate_item[checker]"]')
    if (checker instanceof HTMLInputElement) checker.value = "true"
  }

  handleFocusIn(event) {
    const target = event.target
    if (!this.isEstimateItemInput(target)) return

    this.selectAllInputText(target)
  }

  handleKeydown(event) {
    const active = event.target
    if (!this.isEstimateItemInput(active)) return
    if (!NAV_KEYS.has(event.key)) return
    if (event.isComposing) return
    if ((event.key === "ArrowLeft" || event.key === "ArrowRight") &&
        (event.shiftKey || event.ctrlKey || event.altKey || event.metaKey)) return

    if (event.key === "Enter") {
      event.preventDefault()
      if (event.repeat) return
      this.selectAllInputText(active)
      this.enterSaveInput = active
      active.form.requestSubmit()
      return
    }

    if (event.key === "Tab") {
      event.preventDefault()
      this.moveHorizontal(active, event.shiftKey ? -1 : 1)
      return
    }

    if (event.key === "ArrowUp") {
      event.preventDefault()
      this.moveVertical(active, -1)
      return
    }

    if (event.key === "ArrowDown") {
      event.preventDefault()
      this.moveVertical(active, 1)
      return
    }

    if (event.key === "ArrowLeft" && this.shouldMoveHorizontally(active, "left")) {
      event.preventDefault()
      this.moveHorizontal(active, -1)
      return
    }

    if (event.key === "ArrowRight" && this.shouldMoveHorizontally(active, "right")) {
      event.preventDefault()
      this.moveHorizontal(active, 1)
    }
  }

  moveHorizontal(input, delta) {
    const inputs = this.visibleGridInputs()
    const index = inputs.indexOf(input)
    if (index < 0) return

    const target = inputs[index + delta]
    if (target) this.focusInput(target)
  }

  moveVertical(input, delta) {
    const rows = this.visibleRows()
    const row = input.closest(".navigable")
    const rowIndex = rows.indexOf(row)
    if (rowIndex < 0) return

    const nextRow = rows[rowIndex + delta]
    if (!nextRow) return

    const cellIndex = input.closest(".navigable-cell")?.dataset?.index
    const target =
      nextRow.querySelector(`.navigable-cell[data-index="${cellIndex}"] input:not([type="hidden"]):not([type="checkbox"])`) ||
      nextRow.querySelector('input:not([type="hidden"]):not([type="checkbox"])')

    if (target instanceof HTMLInputElement) this.focusInput(target)
  }

  focusInput(input) {
    if (!(input instanceof HTMLInputElement)) return

    input.focus()
    this.selectAllInputText(input)
  }

  restoreSaveFocus() {
    const savedInput = this.enterSaveInput
    if (!savedInput || savedInput.isConnected) return

    const replacement = document.getElementById(savedInput.id)
    if (!this.isEstimateItemInput(replacement)) return

    this.enterSaveInput = null
    if (document.activeElement !== document.body && document.activeElement !== document.documentElement) return

    replacement.focus({ preventScroll: true })
    this.selectAllInputText(replacement)
  }

  selectAllInputText(input) {
    if (!(input instanceof HTMLInputElement)) return
    this.lastSelectedInput = input
    if (typeof input.select === "function") input.select()
  }

  shouldMoveHorizontally(input, direction) {
    const start = input.selectionStart
    const end = input.selectionEnd
    if (start == null || end == null) return true
    if (start !== end) return false

    if (direction === "left") return start === 0
    return end === (input.value || "").length
  }

  visibleRows() {
    return Array.from(this.element.querySelectorAll("form.edit_estimate_item tr.navigable")).filter((row) => row.offsetParent !== null)
  }

  visibleGridInputs() {
    return this.visibleRows().flatMap((row) => (
      Array.from(row.querySelectorAll(".navigable-cell input")).filter((input) => this.isEstimateItemInput(input))
    ))
  }

  isEstimateItemInput(input) {
    if (!(input instanceof HTMLInputElement)) return false
    if (!this.element.contains(input)) return false
    if (!input.closest("form.edit_estimate_item")) return false
    if (!input.closest(".navigable-cell")) return false
    if (input.disabled || input.readOnly) return false

    return !NON_EDIT_INPUT_TYPES.has((input.type || "").toLowerCase())
  }
}
